import React, { useContext, useState } from 'react';
// 1. Import the dialog component and MUI Button for consistency
import ShowAppsDialog from "../components/dialogs/ShowAppsDialog" 
import Button from '@mui/material/Button';
import AppsTileList from './components/AppsTileList';
import useApps from '../hooks/useApps';
import { UserContext } from '../context/UserContext';
import AppsChooser from './AppsChooser';
import CommonLayout from '../components/CommonLayout';

import { FaEdit, FaShapes } from 'react-icons/fa';

interface App {
  _id: string;
  key: string;
  title: string;
  icon: string;
  status: string;
  appToken: string;
}

interface Department {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
  apps: (App | string)[];
  hospitalName?: string;
  hospitalId?: string;
}

interface Hospital {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
  departments: Department[];
}

interface ExtendedDepartment extends Department {
  hospitalName: string;
  hospitalId: string;
  hospitalEnglishName: string;
}

const LoggedInApps = () => {
  const { apps, loading, error } = useApps();
  const { hospitals, user } = useContext(UserContext) as { hospitals: Hospital[]; user: any };
  const [showAppsChooser, setShowAppsChooser] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (error) {
    return <div>Error fetching apps</div>;
  }

  // Get all departments with their associated apps
  const getDepartmentApps = (department: Department): App[] => {
    if (!department.apps || department.apps.length === 0) {
      return [];
    }
    
    // Filter apps based on department's app list
    return apps.filter((app: App) =>
      department.apps.some((deptApp: App | string) => {
        // Handle both object references and string IDs
        if (typeof deptApp === 'string') {
          return deptApp === app._id || deptApp === app.key;
        }
        return deptApp._id === app._id || deptApp.key === app.key;
      })
    );
  };

  const handleChooseApps = (department: Department) => {
    setSelectedDepartment(department);
    setShowAppsChooser(true);
  };

  const handleAppsChooserClose = () => {
    setShowAppsChooser(false);
    setSelectedDepartment(null);
  };

  // Check if user has permission to edit apps for a specific department
  const hasEditPermission = (departmentId: string): boolean => {
    if (!user) return false;
    
    // Super admin can edit everything
    if (user.isSuperAdmin) return true;
    
    // Check if user has ADMIN role for this specific department
    return user.roles?.some((role: any) =>
      role.department === departmentId && role.role === 'ADMIN'
    ) || false;
  };

  // Get all departments from all hospitals
  const allDepartments: ExtendedDepartment[] = hospitals.flatMap(hospital =>
    hospital.departments?.map(dept => ({
      ...dept,
      hospitalName: hospital.nameHebrew,
      hospitalId: hospital._id,
      hospitalEnglishName: hospital.nameEnglish
    })) || []
  );

  if (loading) {
    return (
      <div>
        <AppsTileList apps={[]} loading={true} />
      </div>
    );
  }

  const userHasDepartments = allDepartments.length > 0;
  const userHasApps = allDepartments.some(d => getDepartmentApps(d).length > 0);

  if (userHasDepartments && !userHasApps) {
    const adminDepartments = allDepartments.filter(department => hasEditPermission(department._id));
    const hasAdminPermissions = adminDepartments.length > 0;

    return (
      <>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          minHeight: '60dvh'
        }}>
          <h2>עדיין לא נוספו אפליקציות לשימוש המחלקה</h2>
          <p style={{
            fontSize: '1.2rem',
            maxWidth: '600px',
            marginBottom: '30px',
            direction: 'rtl'
          }}>
            {hasAdminPermissions
              ? 'זה הזמן להכיר את האפליקציות שלנו ולהוסיף כלים שיעזרו למחלקה שלך לעבוד מדוייק, מהר ויעיל יותר. הכנו גם סיור קצר, שיעזור לך להכיר אותן טוב יותר.'
              : 'זה הזמן להכיר את האפליקציות שלנו ולבקש ממנהל המחלקה להוסיף כלים שיעזרו למחלקה שלך לעבוד מדוייק, מהר ויעיל יותר. הכנו גם סיור קצר, שיעזור לך להכיר אותן טוב יותר.'}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center', width: '100%', maxWidth: '300px' }}>
                                    <button 
              onClick={()=>setIsDialogOpen(true)} 
              style={{
                backgroundColor: 'white',
                color: '#0C3058',
                border: '1px solid #0C3058',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                fontWeight: 'bold',
                width: '100%'
            }}>
              סיור אפליקציות
            </button>
            {hasAdminPermissions && adminDepartments.map(department => (
              <button
                key={department._id}
                onClick={() => handleChooseApps(department)}
                style={{
                  backgroundColor: '#0C3058',
                  color: 'white',
                  border: '1px solid #0C3058',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                  width: '100%'
                }}
              >
                הוסף אפליקציות ל{department.nameHebrew}
              </button>
            ))}
          </div>
        </div>
        <ShowAppsDialog 
          isOpen={isDialogOpen} 
          onClose={() => setIsDialogOpen(false)} 
        />
        {showAppsChooser && selectedDepartment && (
          <AppsChooser
            department={selectedDepartment as ExtendedDepartment}
            onClose={handleAppsChooserClose}
          />
        )}
      </>
    );
  }

  return (
    <>
    <div>
      {allDepartments.length === 0 ? (
        <AppsTileList apps={apps} loading={loading} />
      ) : (
        allDepartments.map((department) => {
          const departmentApps = getDepartmentApps(department);

          return (
            <div key={department._id} style={{ marginBottom: '40px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                marginBottom: '20px',
                direction: 'rtl'
              }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  margin: 0,
                  textAlign: 'right' as const,
                  paddingLeft: '30px'
                }}>
                  {department.nameHebrew}, בית חולים {department.hospitalName}
                </h2>
                {hasEditPermission(department._id) && (
                  <button onClick={() => handleChooseApps(department)}
                    style={{color: '#0C3058', border: 'none', background: 'none', cursor: 'pointer'}} title="בחר אפליקציות">
                    <FaEdit size={16} />
                  </button>
                )}
              </div>
              
              <div>
                {departmentApps.length > 0 ? (
                  <AppsTileList
                    apps={departmentApps}
                    loading={false}
                    hospitalEnglishName={department.hospitalEnglishName}
                  />
                ) : (
                  <div style={{ direction: 'rtl', textAlign: 'right'}}>
                    לא נבחרו אפליקציות למחלקה זו. באפשרותך לבחור אפליקציות ע"י לחיצה על כפתור העריכה
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
      
      {showAppsChooser && selectedDepartment && (
        <AppsChooser
          department={selectedDepartment as ExtendedDepartment}
          onClose={handleAppsChooserClose}
        />
      )}
    </div>
    </>
  );
};

export default LoggedInApps;